/**
 * El saldo a favor de un cliente: subvenciones que pagan sus facturas.
 *
 * POR QUÉ EXISTE
 * --------------
 * Angel, 7-oct-2026: "hay clientes que han obtenido una subvención que da el
 * gobierno para pagar este tipo de servicios... debo tener un campo por
 * cliente con saldo a favor que pueda ir modificando".
 *
 * Hasta ahora se llevaba a mano y de dos formas distintas —una línea en
 * negativo, o un descuento del 100 %— y lo que quedaba se escribía **en el
 * texto de la descripción**: "quedan 4 licencias anuales por compensar".
 *
 * QUÉ SE ENSEÑA Y POR QUÉ
 * -----------------------
 * El número grande, sí, pero sobre todo **el libro de movimientos**. Es
 * dinero de una subvención pública: cuando alguien pregunte por qué pone
 * 1.250 y no 1.500, el número solo no contesta.
 *
 * Y si el saldo guardado no cuadra con la suma de los apuntes, se dice. Un
 * número del que no te puedes fiar es peor que no enseñarlo.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { AlertTriangle, Plus, Minus, Wallet } from 'lucide-react';
import { mensajeDeError } from '@/lib/errores';
import { dinero } from '@/lib/cobros';

/** En español: "1.250,00 €". El campo se teclea con coma, los textos igual. */
const euros = (n) => `${(Number(n) || 0).toLocaleString('es-ES', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})} €`;

const fecha = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('es-ES');
};

const ETIQUETA = {
  alta: 'Alta',
  consumo: 'Factura',
  correccion: 'Corrección',
};

export function SaldoAFavor({ customerId, customerName }) {
  const [datos, setDatos] = useState(null);
  const [abierto, setAbierto] = useState(false);
  const [suma, setSuma] = useState(true);
  const [importe, setImporte] = useState('');
  const [concepto, setConcepto] = useState('');
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(`/customers/${customerId}/saldo`);
      setDatos(data);
    } catch (error) {
      // Sin saldo no es un error que merezca un aviso: la mayoría de
      // clientes no tienen subvención y el bloque simplemente no sale.
      setDatos(null);
    }
  }, [customerId]);

  useEffect(() => { if (customerId) cargar(); }, [customerId, cargar]);

  const cantidad = dinero(importe);
  const pega = !concepto.trim()
    ? 'Pon de dónde sale o en qué se va.'
    : cantidad <= 0
      ? 'El importe tiene que ser mayor que 0 €.'
      : (!suma && cantidad > (datos?.saldo ?? 0))
        ? `Solo quedan ${euros(datos?.saldo)}.`
        : null;

  const guardar = async () => {
    setGuardando(true);
    try {
      const { data } = await axiosInstance.post(`/customers/${customerId}/saldo`, {
        importe: suma ? cantidad : -cantidad,
        concepto: concepto.trim(),
        motivo: motivo.trim() || null,
      });
      toast.success(data.message);
      setAbierto(false);
      setImporte(''); setConcepto('');
      cargar();
    } catch (error) {
      toast.error(mensajeDeError(error));
    } finally {
      setGuardando(false);
    }
  };

  const hayAlgo = datos && (datos.saldo > 0 || (datos.movimientos || []).length > 0);

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Wallet className="w-4 h-4 text-slate-500" />
                Saldo a favor
              </CardTitle>
              <p className="text-sm text-gray-500">
                Subvenciones que pagan las facturas de este cliente.
              </p>
            </div>
            <Button
              variant="outline" size="sm"
              onClick={() => { setSuma(true); setMotivo(datos?.motivo || ''); setAbierto(true); }}
              className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
              data-testid="mover-saldo"
            >
              Mover saldo
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!hayAlgo ? (
            <p className="text-sm text-gray-500">
              Este cliente no tiene saldo. Si le han concedido una subvención,
              dásela de alta con «Mover saldo».
            </p>
          ) : (
            <>
              <div className="flex items-baseline gap-3">
                <span className="text-2xl font-bold text-slate-900" data-testid="saldo-actual">
                  {euros(datos.saldo)}
                </span>
                {datos.motivo && (
                  <span className="text-sm text-gray-500">{datos.motivo}</span>
                )}
              </div>

              {/* El guardado es la consecuencia; la fuente es el libro. Si no
                  cuadran, se dice: un número del que no te puedes fiar es
                  peor que no enseñarlo. */}
              {datos.cuadra === false && (
                <p className="mt-2 text-sm text-amber-700 flex items-start gap-1.5">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  Los movimientos suman {euros(datos.saldo_segun_movimientos)} y
                  aquí pone {euros(datos.saldo)}. Manda el libro: revísalo antes
                  de usarlo.
                </p>
              )}

              {(datos.movimientos || []).length > 0 && (
                <div className="mt-4 border rounded-lg divide-y">
                  {datos.movimientos.slice(0, 8).map((m) => (
                    <div key={m.id} className="flex items-center justify-between gap-3 p-2.5 text-sm">
                      <div className="min-w-0">
                        <p className="truncate">
                          {m.concepto}
                          {m.invoice_number && (
                            <span className="ml-1 text-gray-500">· {m.invoice_number}</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">
                          {fecha(m.fecha)} · {ETIQUETA[m.tipo] || m.tipo}
                          {m.usuario && ` · ${m.usuario}`}
                        </p>
                      </div>
                      <div className="text-right whitespace-nowrap">
                        <span className={m.importe >= 0 ? 'font-medium text-emerald-700'
                          : 'font-medium text-slate-900'}>
                          {m.importe >= 0 ? '+' : '−'}{euros(Math.abs(m.importe))}
                        </span>
                        <p className="text-xs text-gray-500">queda {euros(m.saldo_despues)}</p>
                      </div>
                    </div>
                  ))}
                  {datos.movimientos.length > 8 && (
                    <p className="p-2 text-xs text-gray-500">
                      Se enseñan los 8 últimos de {datos.movimientos.length}.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={abierto} onOpenChange={setAbierto}>
        <DialogContent className="max-w-md" data-testid="dialogo-saldo">
          <DialogHeader>
            <DialogTitle>Mover saldo a favor</DialogTitle>
            <DialogDescription>
              {customerName} · ahora tiene {euros(datos?.saldo)}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button" variant={suma ? 'default' : 'outline'}
              onClick={() => setSuma(true)}
              className={suma ? 'bg-[#9C0F17] hover:bg-[#7A0C12] text-white' : ''}
              data-testid="saldo-sumar"
            >
              <Plus className="w-4 h-4 mr-1" /> Añadir
            </Button>
            <Button
              type="button" variant={suma ? 'outline' : 'default'}
              onClick={() => setSuma(false)}
              className={suma ? '' : 'bg-[#9C0F17] hover:bg-[#7A0C12] text-white'}
              data-testid="saldo-restar"
            >
              <Minus className="w-4 h-4 mr-1" /> Quitar
            </Button>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="saldo-importe">¿Cuánto? *</Label>
            {/* `text` y no `number`: con number, teclear "1.250,00" deja el
                valor vacío y el botón no se habilita nunca. Ya pasó en el
                diálogo de cobros. */}
            <Input
              id="saldo-importe" type="text" inputMode="decimal"
              value={importe} onChange={(e) => setImporte(e.target.value)}
              placeholder="0,00"
              data-testid="saldo-importe"
            />
            {cantidad > 0 && (
              <p className="text-xs text-slate-500">
                Quedaría en{' '}
                <strong>
                  {euros(suma ? (datos?.saldo ?? 0) + cantidad
                    : Math.max(0, (datos?.saldo ?? 0) - cantidad))}
                </strong>.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="saldo-concepto">¿De dónde sale o en qué se va? *</Label>
            <Input
              id="saldo-concepto" type="text" value={concepto}
              onChange={(e) => setConcepto(e.target.value)}
              placeholder="Ej: Subvención Kit Digital, resolución de 12/09"
              data-testid="saldo-concepto"
            />
            <p className="text-xs text-slate-500">
              Es dinero de una subvención: dentro de un año esto será lo único
              que explique el movimiento.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="saldo-motivo">De qué subvención es (opcional)</Label>
            <Input
              id="saldo-motivo" type="text" value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ej: Kit Digital 2026"
              data-testid="saldo-motivo"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAbierto(false)} disabled={guardando}>
              Cancelar
            </Button>
            <Button
              onClick={guardar} disabled={Boolean(pega) || guardando}
              className="bg-[#9C0F17] hover:bg-[#7A0C12] text-white"
              title={pega || undefined}
              data-testid="confirmar-saldo"
            >
              {guardando ? 'Guardando...' : 'Guardar movimiento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SaldoAFavor;
